import React, { useRef, useEffect, useState } from 'react';
import {useNotificationMessage} from "../../hooks/NotificationContext";
import {applyEffect} from "../../utils/ImageEffect";
import {basicDownloadImage} from "../../utils";

interface IPreviewProps {
    isLight: boolean;
    selectedEffect: string;
}

const canvasSize = 500;
export const Preview: React.FC<IPreviewProps> = ({isLight, selectedEffect}) => {
    const {showNotification} = useNotificationMessage();

    const originalCanvasRef = useRef<HTMLCanvasElement>(null);
    const processedCanvasRef = useRef<HTMLCanvasElement>(null);

    const [currentImage, setCurrentImage] = useState<HTMLImageElement>();

    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [intensity, setIntensity] = useState(0.5);

    useEffect(() => {// 初始化
        const originalCanvas = originalCanvasRef.current;
        const processedCanvas = processedCanvasRef.current;
        if (!originalCanvas || !processedCanvas) return;

        const originalCtx = originalCanvas.getContext('2d');
        const processedCtx = processedCanvas.getContext('2d');
        if (!originalCtx || !processedCtx) return;

        // 清除画布
        originalCtx.clearRect(0, 0, originalCanvas.width, originalCanvas.height);
        processedCtx.clearRect(0, 0, processedCanvas.width, processedCanvas.height);

        const img = new Image();
        const processImg = new Image();
        img.src = '/images/20240323183659590520.png';
        processImg.src = '/images/processed_image.png'
        img.onload = () => {
            setCurrentImage(img);
            // 绘制到原始Canvas
            drawImageToCanvas(img, originalCanvas);
            setIsImageLoaded(true);
        };
        processImg.onload = () => {
            // 绘制到处理Canvas
            drawImageToCanvas(processImg, processedCanvas);
        }
    }, []);

    // 监听图像加载事件
    useEffect(() => {
        const handleImageLoaded = (event: any) => {
            if (event.detail && event.detail.image) {
                const img = event.detail.image;

                if (originalCanvasRef.current && processedCanvasRef.current) {
                    const originalCtx = originalCanvasRef.current.getContext('2d');
                    const processedCtx = processedCanvasRef.current.getContext('2d');
                    if (!originalCtx || !processedCtx) return ;

                    // 清除画布
                    originalCtx.clearRect(0, 0, originalCanvasRef.current.width, originalCanvasRef.current.height);
                    processedCtx.clearRect(0, 0, processedCanvasRef.current.width, processedCanvasRef.current.height);

                    setCurrentImage(img);

                    // 绘制到原始Canvas
                    drawImageToCanvas(img, originalCanvasRef.current);
                    // 复制到处理Canvas
                    drawImageToCanvas(img, processedCanvasRef.current);

                    setIsImageLoaded(true);
                }
            }
        };

        window.addEventListener('imageLoaded', handleImageLoaded);
        return () => {
            window.removeEventListener('imageLoaded', handleImageLoaded);
        };
    }, []);


    // 强度值改变
    const handleIntensityChange = (e: any) => {
        setIntensity(Number(e.target.value));
    };

    // 绘制图像到Canvas
    const drawImageToCanvas = (img: HTMLImageElement, canvas: HTMLCanvasElement) => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return ;

        // 计算缩放比例以保持宽高比
        const scale = Math.min(
            canvasSize / img.width,
            canvasSize / img.height
        );

        const width = img.width * scale;
        const height = img.height * scale;

        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, width, height);
    };

    // 应用效果
    const handleApplyEffect = () => {
        if (!isImageLoaded) {
            showNotification('Please Upload Image', 'error');
            return;
        }
        if (!selectedEffect) {
            showNotification('Please Select Effect', 'error');
            return;
        }

        const processedCanvas = processedCanvasRef.current;
        if (!processedCanvas) return;

        const processedCtx = processedCanvas.getContext('2d');
        if (!processedCtx || !currentImage) return;

        // 复制原始图像到处理Canvas
        drawImageToCanvas(currentImage, processedCanvas);

        // 应用选择的效果
        applyEffect(selectedEffect, processedCanvas, intensity);

        // 显示成功通知
        showNotification(`${selectedEffect} applied`);
    };

    const saveImage = () => {
        if (!isImageLoaded) {
            showNotification('没有可保存的图像', 'error');
            return;
        }

        const processedCanvas = processedCanvasRef.current;
        if (!processedCanvas) return ;

        basicDownloadImage(processedCanvas.toDataURL('image/png', 10), `IMG_${Date.now()}.png`);
        showNotification('图片已保存！');
    };

    const resetImage = () => {
        if (!isImageLoaded) {
            showNotification('没有可重置的图像', 'error');
            return;
        }

        const processedCanvas = processedCanvasRef.current;
        if (!processedCanvas) return;

        const processedCtx = processedCanvas.getContext('2d');
        if (!processedCtx || !currentImage) return ;

        // 复制原始图像到处理Canvas
        drawImageToCanvas(currentImage, processedCanvas);
        showNotification('已重置图像');
    };

    return (
        <section className="py-16 px-4">{/*bg-gradient-to-br from-indigo-50 to-blue-50*/}
            <div className="container mx-auto">
                <h2 className={`text-3xl font-bold text-center mb-12 ${isLight ? 'text-gray-800' : 'text-gray-500'}`}>
                    Live<span className="text-indigo-600">&nbsp;Preview</span></h2>

                <div className="flex m-auto md:w-1/2 justify-center gap-8 items-center">
                    <div className="lg:w-1/2 text-[18pt] font-bold">
                        <div className="home-canvas-container floating">
                            <canvas
                                ref={originalCanvasRef}
                                id="originalCanvas"
                                className="bg-white w-[100%] h-[100%]"
                            ></canvas>
                        </div>
                        <p className={`text-center mt-2 ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>Original Image</p>
                    </div>

                    <div className="flex flex-col items-center justify-center">
                        <button
                            id="applyEffect"
                            className="px-3 py-1 bg-indigo-600 text-white rounded-[25px] hover:bg-indigo-700 transition"
                            onClick={handleApplyEffect}
                        >
                            <i className="fas fa-random text-2xl"></i>
                        </button>
                        <p className={`${isLight ? 'text-gray-600' : 'text-gray-300'} text-sm select-none`}>Apply</p>
                    </div>

                    <div className="lg:w-1/2 text-[18pt] font-bold">
                        <div className="home-canvas-container floating">
                            <canvas
                                ref={processedCanvasRef}
                                id="processedCanvas"
                                className="bg-white w-[100%] h-[100%]"
                            ></canvas>
                        </div>
                        <p className={`text-center mt-2 ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>Processed Image</p>
                    </div>
                </div>

                <div className="w-[30%] m-auto">
                    <label form="intensity"
                           className={`block  text-[12pt] font-medium ${isLight ? 'text-gray-700' : 'text-gray-400'} mb-2`}>Effect intensity
                    </label>
                    <input type="range" id="intensity" min="0" step="0.1" max="5"
                           value={intensity} onChange={handleIntensityChange}
                           className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-thumb"/>
                </div>

                <div className="mt-12 flex justify-center text-[12pt] space-x-4">
                    <button
                        id="saveBtn"
                        className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center"
                        onClick={saveImage}
                    >
                        <i className="fas fa-download mr-2"></i> Save
                    </button>
                    <button
                        id="resetBtn"
                        className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition flex items-center"
                        onClick={resetImage}
                    >
                        <i className="fas fa-undo mr-2"></i> Reset
                    </button>
                </div>
            </div>
        </section>
    );
};
